import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { filter, groupBy, get } from 'lodash';
import {
    Button,
    IconButton,
    KeyboardArrowLeftIcon,
    KeyboardArrowRightIcon,
    SettingsIcon,
    NotificationsIcon,
    AppsIcon,
    HelpOutlineIcon,
} from 'tuui';
import { ExternalApps } from 'taboola-ultimate-ui';
import { Collapsible } from 'taboola-ultimate-ui';
import { useAvailableItems, useNavigate } from '../../hooks';
import { useNonAuthAccess } from '../../hooks/useNonAuthAccess';
import { useAccount } from '../../modules/taboola-common-frontend-modules/account-configurations';
import { logout, usePermissions, userSelector } from '../../modules/taboola-common-frontend-modules/authentication';
import { FormattedMessage, useIntl } from '../../modules/taboola-common-frontend-modules/i18n';
import { isSeparateAppInstanceForbidden } from '../../modules/taboola-common-frontend-modules/iframe/utils/isSeparateAppInstanceForbidden';
import { AdminDropdownHeadless } from '../AdminDropdown/AdminDropdown';
import { useShowHeader } from '../AppHeader/hooks/useShowHeader';
import { useChatAgentPermitted } from '../ChatAgent/hooks/useChatAgentPermitted';
import { NotificationsCenter } from '../NotificationsCenter/NotificationsCenter';
import { useShowNotificationsCenter } from '../NotificationsCenter/hooks/useShowNotificationsCenter';
import useAvailableRoutes from '../RouteList/hooks/useAvailableRoutes';
import { UserMenu } from '../UserMenu';
import { HelpDropDown } from './HelpDropDown/HelpDropDown';
import { ActiveUserCaption } from './components/ActiveUserCaption/ActiveUserCaption';
import { ActiveUserIcon } from './components/ActiveUserIcon/ActiveUserIcon';
import { IntercomMessageCounter } from './components/IntercomMessageCounter/IntercomMessageCounter';
import { NavigationSideBarLogo } from './components/NavigationSideBarLogo/NavigationSideBarLogo';
import { NavigationSideItem } from './components/NavigationSideItem/NavigationSideItem';
import { NotificationsBadgeCounter } from './components/NotificationsBadgeCounter/NotificationsBadgeCounter';
import { PopupSideItem } from './components/PopupSideItem/PopupSideItem';
import { useIntercomMenu } from './hooks/useIntercomMenu';
import { localizeLinks } from './utils/utils';
import styles from './NavigationSideBar.module.scss';

const LOGOUT_PATH = '/logout';

const getNavItems = items => {
    const navItems = filter(items, ({ hideNavItem, hidden }) => !hideNavItem && !hidden);
    const navItemsByGroupId = groupBy(navItems, item => item.groupId || item.id);
    return Object.entries(navItemsByGroupId).map(([groupId, navItems]) => ({
        groupId,
        navItems,
        isDropdown: navItems.length > 1,
    }));
};

const NotificationsCenterStyled = props => (
    <NotificationsCenter
        {...props}
        headerClassName={styles['notifications-header']}
        dropdownClassName={styles['notifications-dropdown']}
    />
);

const ExternalAppsStyled = props => (
    <ExternalApps
        {...props}
        AppsIconComponent={() => null}
        containerClassName={styles['external-app-container']}
        bodyClassName={styles['external-app-body']}
        withBackgroundOverlay={false}
    />
);

const AdminDropdownHeadlessStyled = props => (
    <AdminDropdownHeadless {...props} menuStyles={{ paddingBottom: 0.1, position: 'unset' }} />
);

const UserDropdown = ({ user, localLegal, onLogoutButtonClick, ...rest }) => {
    return (
        <UserMenu
            headless
            userName={user['full_name']}
            email={user.username}
            topLinks={[localLegal.privacy]}
            bottomLinks={[localLegal.termOfUse, localLegal.cookiesConsent]}
            buttonText={<FormattedMessage id="app.actionButtons.signOut" />}
            onButtonClick={onLogoutButtonClick}
            popupClassName={styles['user-menu-popup']}
            withBackgroundOverlay={false}
            {...rest}
        />
    );
};

const transitionDuration = 1000 * Number.parseFloat(styles.animationTime);

export const NavigationSideBar = ({ config, onlyLogout = false }) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const user = useSelector(userSelector);
    const account = useAccount();
    const { id: accountId } = account;
    const [collapsed, setCollapsed] = useState(true);
    const { legal, routingMap, externalApps } = config;
    const availableRoutes = useAvailableRoutes(routingMap);
    const navigationItems = useMemo(() => getNavItems(availableRoutes), [availableRoutes]);
    const localLegal = useMemo(() => localizeLinks(legal, formatMessage), [legal, formatMessage]);
    const navigate = useNavigate();
    const onLogoutButtonClick = () => navigate(LOGOUT_PATH);
    const localExternalApps = useMemo(
        () => Object.values(localizeLinks(externalApps, formatMessage, { accountId, selectedAccount: account })),
        [externalApps, formatMessage, accountId, account]
    );
    const apps = useAvailableItems(localExternalApps, { selectedAccount: account });
    const gtmAttrs = label => ({
        'data-metrics-event-name': 'usability',
        'data-metrics-component': 'AppsMenu',
        'data-metrics-column-name': label,
    });
    const { showNotificationsCenter } = useShowNotificationsCenter();
    const { pathname } = useLocation();
    const { active: intercomActive, toggle: intercomToggle, close: closeIntercom } = useIntercomMenu();
    const showHeaderForModule = useShowHeader(config);
    const nonAuthAccess = useNonAuthAccess();
    const shouldRenderUserMenu = !isSeparateAppInstanceForbidden();
    const isChatAgentPermitted = useChatAgentPermitted();
    const isHelpCenterAgentPermitted = usePermissions('HELP_CENTER_AGENT');
    const showHelpCenterAgent = isHelpCenterAgentPermitted && isChatAgentPermitted;

    // Once we are on logout path we do log out
    useEffect(() => {
        if (pathname !== LOGOUT_PATH) {
            return;
        }

        dispatch(logout());
    }, [pathname, dispatch]);

    if (!showHeaderForModule || nonAuthAccess) {
        return null;
    }
    return (
        <div className={styles['container']}>
            <Collapsible
                collapsed={collapsed}
                direction={Collapsible.DIRECTION.HORIZONTAL}
                collapsedSize={56}
                expandedSize={200}
                className={styles['collapsible']}
                transitionDuration={transitionDuration}
            >
                <div className={styles['content-wrapper']}>
                    <NavigationSideBarLogo collapsed={collapsed} />
                    <div className={styles['navigation-items']}>
                        <div className={styles['top-items']}>
                            {navigationItems.map(({ navItems, groupId }) => (
                                <NavigationSideItem
                                    key={get(navItems, '0.id')}
                                    item={get(navItems, '0')}
                                    subItems={navItems}
                                    collapsed={collapsed}
                                    dataAutomationId={groupId}
                                />
                            ))}
                        </div>
                        <div className={styles['bottom-items']}>
                            {!onlyLogout && (
                                <>
                                    <PopupSideItem
                                        item={{
                                            icon: <HelpOutlineIcon />,
                                            caption: {
                                                id: showHelpCenterAgent
                                                    ? 'side.menu.help.and.assistance'
                                                    : 'side.menu.intercom',
                                                defaultMessage: showHelpCenterAgent ? 'Help & Assistance' : 'Help',
                                            },
                                            badgeComponent: IntercomMessageCounter,
                                        }}
                                        collapsed={collapsed}
                                        dropdownComponent={showHelpCenterAgent ? HelpDropDown : null}
                                        handler={!showHelpCenterAgent ? intercomToggle : closeIntercom}
                                        open={!showHelpCenterAgent ? intercomActive : false}
                                        dataAutomationId="help-outline-menu"
                                        stateful={!showHelpCenterAgent}
                                    />
                                    <div className={styles['divider']} />
                                    {showNotificationsCenter && (
                                        <PopupSideItem
                                            item={{
                                                icon: <NotificationsIcon />,
                                                caption: {
                                                    id: 'side.menu.notifications',
                                                    defaultMessage: 'Notifications',
                                                },
                                                badgeComponent: NotificationsBadgeCounter,
                                            }}
                                            collapsed={collapsed}
                                            dropdownComponent={NotificationsCenterStyled}
                                            handler={closeIntercom}
                                            dataAutomationId="notification-badge-counter"
                                        />
                                    )}
                                    <PopupSideItem
                                        item={{
                                            icon: <AppsIcon />,
                                            caption: { id: 'side.menu.apps', defaultMessage: 'Apps' },
                                        }}
                                        collapsed={collapsed}
                                        dropdownComponent={ExternalAppsStyled}
                                        dropdownProps={{ apps, generateLinkProps: gtmAttrs }}
                                        handler={closeIntercom}
                                        dataAutomationId="external-apps-menu"
                                    />
                                    <PopupSideItem
                                        item={{
                                            icon: <SettingsIcon />,
                                            caption: { id: 'side.menu.admin', defaultMessage: 'Admin' },
                                        }}
                                        collapsed={collapsed}
                                        dropdownComponent={AdminDropdownHeadlessStyled}
                                        dropdownProps={{ availableRoutes }}
                                        handler={closeIntercom}
                                        dataAutomationId="admin-dropdown-menu"
                                    />
                                </>
                            )}
                            {!nonAuthAccess && user['full_name'] && shouldRenderUserMenu && (
                                <PopupSideItem
                                    item={{
                                        icon: <ActiveUserIcon />,
                                        captionComponent: ActiveUserCaption,
                                    }}
                                    collapsed={collapsed}
                                    dropdownComponent={UserDropdown}
                                    dropdownProps={{ user, localLegal, onLogoutButtonClick }}
                                    handler={closeIntercom}
                                    dataAutomationId="user-dropdown-menu"
                                    ariaLabel="User Logo"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Collapsible>
            <div className={styles['collapsible-control']}>
                <IconButton
                    onClick={() => setCollapsed(prev => !prev)}
                    variant={Button.variant.ghost}
                    className={styles['icon-button']}
                    size={Button.size.lg}
                >
                    {collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
                </IconButton>
            </div>
        </div>
    );
};
